import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const SupportChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "👋 Hi! Welcome to Xtream Store. How can I help you today?",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getBotResponse = (userMessage) => {
    const msg = userMessage.toLowerCase();
    
    // Simple keyword-based responses
    if (msg.includes('hi') || msg.includes('hello') || msg.includes('hey')) {
      return "Hello! 👋 I'm here to help you with any questions about our premium accounts. What would you like to know?";
    } else if (msg.includes('price') || msg.includes('cost') || msg.includes('how much')) {
      return "Our prices range from ₹129 to ₹299 depending on the service. You can check the exact price on each product page. All accounts come with validity periods mentioned on the product.";
    } else if (msg.includes('validity') || msg.includes('how long') || msg.includes('duration')) {
      return "Each account has its own validity period mentioned on the product page. Most accounts are valid for 30-90 days. The exact duration is displayed clearly before purchase.";
    } else if (msg.includes('payment') || msg.includes('pay') || msg.includes('razorpay')) {
      return "We accept payments through Razorpay - you can pay using credit/debit cards, UPI, net banking, and wallets. All transactions are 100% secure.";
    } else if (msg.includes('delivery') || msg.includes('receive') || msg.includes('email')) {
      return "Your account credentials will be delivered instantly to your email after successful payment. Make sure to check your spam folder if you don't see it in your inbox.";
    } else if (msg.includes('refund') || msg.includes('return')) {
      return "For refunds or account-related issues, please contact our support team:\n📧 Support@xtreamstore.in\n💬 Telegram: https://t.me/Xtreamstoresupport";
    } else if (msg.includes('account') || msg.includes('credentials')) {
      return "After purchase, you'll receive your account credentials via email within minutes. Each account is verified and tested before delivery.";
    } else if (msg.includes('help') || msg.includes('support') || msg.includes('agent')) {
      return "I can help you with common questions! If you need personalized assistance, please contact our support team:\n\n📧 Email: Support@xtreamstore.in\n💬 Telegram: https://t.me/Xtreamstoresupport\n\nOur team will respond within 24 hours!";
    } else if (msg.includes('thank')) {
      return "You're welcome! Happy shopping at Xtream Store! 🎉";
    } else {
      return "I'm here to help with common questions! For personalized support:\n\n📧 Email: Support@xtreamstore.in\n💬 Telegram: https://t.me/Xtreamstoresupport\n\nYou can also ask me about pricing, payments, delivery, or product information.";
    }
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    // Add user message
    const userMsg = {
      id: messages.length + 1,
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMsg]);
    setInputMessage('');

    // Get bot response after a short delay
    setTimeout(() => {
      const botMsg = {
        id: messages.length + 2,
        text: getBotResponse(inputMessage),
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMsg]);
    }, 500);
  };

  const quickReplies = [
    "What's the pricing?",
    "How do I receive my account?",
    "How long is the validity?",
    "Payment methods?"
  ];

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-primary hover:bg-primary/90 text-white shadow-lg flex items-center justify-center transition-all hover:scale-110"
          data-testid="support-chat-button"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-6 right-6 z-50 w-[380px] h-[550px] bg-[#121214] border border-white/10 rounded-2xl shadow-2xl flex flex-col"
          data-testid="support-chat-window"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10 bg-primary rounded-t-2xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-heading font-bold">Support Chat</h3>
                <p className="text-xs opacity-80">We're online!</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/10 p-1 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-2xl ${
                    message.sender === 'user'
                      ? 'bg-primary text-white rounded-br-none'
                      : 'bg-white/5 text-foreground rounded-bl-none'
                  }`}
                >
                  <p className="text-sm whitespace-pre-line">{message.text}</p>
                  <p className="text-xs opacity-60 mt-1">
                    {message.timestamp.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Replies */}
          {messages.length <= 2 && (
            <div className="px-4 pb-2">
              <p className="text-xs text-muted-foreground mb-2">Quick questions:</p>
              <div className="flex flex-wrap gap-2">
                {quickReplies.map((reply, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setInputMessage(reply);
                      setTimeout(() => {
                        const form = document.querySelector('[data-testid="chat-form"]');
                        if (form) form.requestSubmit();
                      }, 100);
                    }}
                    className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
                  >
                    {reply}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <form 
            onSubmit={handleSendMessage} 
            className="border-t border-white/10"
            data-testid="chat-form"
          >
            {/* Support Contact Info */}
            <div className="px-4 pt-3 pb-2 bg-white/5">
              <p className="text-xs text-muted-foreground mb-2">Need personalized help?</p>
              <div className="flex flex-col gap-2 text-xs">
                <a 
                  href="mailto:Support@xtreamstore.in" 
                  className="flex items-center gap-2 text-primary hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  📧 Support@xtreamstore.in
                </a>
                <a 
                  href="https://t.me/Xtreamstoresupport" 
                  className="flex items-center gap-2 text-primary hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="telegram-support-link"
                >
                  💬 Connect on Telegram
                </a>
              </div>
            </div>
            
            <div className="p-4">
              <div className="flex gap-2">
                <Input
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 bg-white/5 border-white/10"
                  data-testid="chat-input"
                />
                <Button 
                  type="submit" 
                  size="icon" 
                  className="btn-primary-glow rounded-full"
                  data-testid="chat-send-button"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default SupportChat;
